import React, { useEffect, useState } from 'react';

interface HealthScoreGaugeProps {
  score: number;
  showTooltip?: boolean;
}

const HealthScoreGauge: React.FC<HealthScoreGaugeProps> = ({ score, showTooltip = true }) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    // Animate score from 0 to target
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setAnimatedScore(prev => {
          if (prev < score) {
            return Math.min(prev + 1, score);
          }
          clearInterval(interval);
          return prev;
        });
      }, 20);
    }, 500);

    return () => clearTimeout(timer);
  }, [score]);

  // Calculate color based on score
  const getColor = () => {
    if (animatedScore >= 80) return '#F7B500'; // Gold
    if (animatedScore >= 60) return '#FFC107'; // Warning yellow
    return '#DC3545'; // Danger red
  };

  // Calculate degrees for conic gradient
  const degrees = (animatedScore / 100) * 360;

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative w-32 h-32">
        <div 
          className="w-full h-full rounded-full flex items-center justify-center"
          style={{
            background: `conic-gradient(${getColor()} 0deg, ${getColor()} ${degrees}deg, #e5e7eb ${degrees}deg)`
          }}
        >
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center">
            <span className="text-4xl font-bold text-kielis-navy">{animatedScore}</span>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && isHovered && (
        <div className="absolute left-full ml-4 top-0 bg-kielis-navy text-white p-4 rounded-lg shadow-xl z-50 min-w-[250px]">
          <div className="font-semibold mb-2">Health Score Calculation:</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>• Risk Score:</span>
              <span>25%</span>
            </div>
            <div className="flex justify-between">
              <span>• Performance:</span>
              <span>30%</span>
            </div>
            <div className="flex justify-between">
              <span>• Compliance:</span>
              <span>25%</span>
            </div>
            <div className="flex justify-between">
              <span>• Liquidity:</span>
              <span>20%</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-white/20 text-xs opacity-80">
            Click for detailed methodology
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthScoreGauge;